import { useStoreApi } from "@xyflow/react";
import { useEffect, useState } from "react";

/** Zoom step the nodes re-render on: 5%. Below what the eye tracks mid-gesture. */
export const ZOOM_STEP = 0.05;

/** Quiet time after the last zoom tick before nodes take the exact zoom. */
export const ZOOM_SETTLE_MS = 150;

export const quantizeZoom = (zoom: number) => Math.round(zoom * 20) / 20;

/**
 * The canvas zoom for sizing chrome: quantised to 5% steps while a gesture
 * runs, exact once it settles. Every node subscribing to the raw zoom
 * (`useStore(s => s.transform[2])`) re-rendered on every wheel/pinch tick,
 * and each re-render rewrote the chrome's counter-scale inline styles —
 * hundreds of forced layouts per frame in WebKit. Subscribing here makes a
 * node render at most once per 5% of zoom during the gesture, and once more
 * with the exact zoom ~150ms after the last tick, so the counter-scale is
 * pixel-identical at rest. Pans never re-render the subscriber.
 */
export function useQuantizedZoom(): number {
  const store = useStoreApi();
  const [zoom, setZoom] = useState(() => store.getState().transform[2]);

  useEffect(() => {
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onZoom = (exact: number) => {
      // Functional update so an unchanged step bails out without a render.
      setZoom((shown) =>
        quantizeZoom(shown) === quantizeZoom(exact)
          ? shown
          : quantizeZoom(exact),
      );
      clearTimeout(settle);
      settle = setTimeout(() => setZoom(exact), ZOOM_SETTLE_MS);
    };
    const unsubscribe = store.subscribe((state, prev) => {
      if (state.transform[2] !== prev.transform[2]) onZoom(state.transform[2]);
    });
    // A tick between the first render and this subscription is not lost.
    onZoom(store.getState().transform[2]);
    return () => {
      clearTimeout(settle);
      unsubscribe();
    };
  }, [store]);

  return zoom;
}
